import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Chip,
  makeStyles
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import BackIcon from "@material-ui/icons/ArrowBack";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import StyledDrawer from "../StyledDrawer";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import EditDialogActions from "../../common/EditDialogActions";
import Form from "../../common/Form";
import UnsavedChangesDialog from "../../dialogs/UnsavedChangesDialog";
import API from "../../../utils/axios";
import { remCalc } from "../../../utils/helpers";
import {
  ACTION_TEXTS,
  COMMON_TAGS,
  DELIVERED_TAGS,
  NOT_DELIVERED_TAGS,
  SMS_TAGS,
  EMAIL_TAGS
} from "../../data/constants";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import mixpanelWrapper from "../../../utils/mixpanel";
import { urlRegex } from "../../../utils/regExps";

const maxHeaderLength = 132;

const triggerOptions = [
  ["project_publish", "Project is published"],
  ["route_start", "Driver starts a route"],
  ["stop_delivered", "Stop delivered"],
  ["stop_skipped", "Stop skipped"]
];

const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    borderBottom: "1px solid #eee",
    boxSizing: "border-box"
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500
  },
  subtitle: {
    lineHeight: "1.1875rem",
    fontWeight: 500
  },
  infoDialog: {
    backgroundColor: "#F7F7F7",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    display: "flex",
    marginBottom: theme.spacing(2),
    "& svg": {
      marginRight: theme.spacing(1.5)
    },
    "& p": {
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      color: "#808080"
    }
  },
  subInfo: {
    fontSize: "0.875rem",
    lineHeight: "1rem",
    marginTop: theme.spacing(1),
    color: theme.palette.secondary.main
  },
  chip: {
    height: remCalc(28),
    color: "#808080",
    fontSize: remCalc(14),
    backgroundColor: "#EEE",
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#EEE",
      color: "#202124"
    }
  }
}));

function TemplatesEditDialog({
  open,
  currentTemplate,
  setCurrentAction,
  templates,
  setTemplates,
  saveDisabled,
  setSaveDisabled
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const initialValues = { message: "", emailHeader: "" };
  const initialSelectValues = { triggered_when: "", channel: "" };

  const [inputValues, setInputValues] = useState({ ...initialValues });
  const [selectValues, setSelectValues] = useState({ ...initialSelectValues });
  const [errors, setErrors] = useState({
    ...initialValues,
    ...initialSelectValues
  });
  const [changesDialog, setChangesDialog] = useState(true);
  const [loading, setLoading] = useState(false);

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    name: "",
    value: ""
  });

  const messageInputRef = useRef();

  const cursorPositionRef = useRef();

  const isDelivered = trigger => {
    return ["stop_delivered", "stop_skipped"].includes(trigger);
  };

  const filteredTags = [
    ...Object.keys(COMMON_TAGS),
    ...(isDelivered(selectValues.triggered_when)
      ? Object.keys(DELIVERED_TAGS)
      : Object.keys(NOT_DELIVERED_TAGS)),
    ...(selectValues.channel === "sms" ? Object.keys(SMS_TAGS) : []),
    ...(selectValues.channel === "email" ? Object.keys(EMAIL_TAGS) : [])
  ];

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    if (e.target.value.trim()) setSaveDisabled(false);
  };

  const handleSelect = e => {
    if (e.target.name === "channel") {
      if (inputValues.message.length && selectValues.channel) {
        setConfirmDialogState({
          open: true,
          name: e.target.name,
          value: e.target.value
        });
        return;
      }
      setInputValues({ ...inputValues, emailHeader: "" });
    }

    setSelectValues({ ...selectValues, [e.target.name]: e.target.value });
    setSaveDisabled(false);
  };

  // Close edit panel
  const handleClose = () => {
    setSaveDisabled(true);
    setCurrentAction("overview");
  };

  const checkForErrors = () => {
    // const isMessageIncludesUrl = inputValues.message
    //   .split(" ")
    //   .some(sentence => sentence.match(urlRegex));
    // console.log(isMessageIncludesUrl);
    const newErrors = {
      message:
        inputValues.message.length < 1 ? "Please fill in this field" : "",
      // : selectValues.channel === "sms" && isMessageIncludesUrl
      // ? "Custom URL's are not allowed."
      // "",
      emailHeader:
        selectValues.channel === "email"
          ? inputValues.emailHeader.length
            ? ""
            : "Please fill in this field"
          : "",
      triggered_when: selectValues.triggered_when
        ? ""
        : "Please select an item on the list",
      channel: selectValues.channel ? "" : "Please select an item on the list"
    };

    setErrors(newErrors);

    return Object.values(newErrors).some(val => val);
  };

  const handleSave = () => {
    if (checkForErrors()) return;

    setLoading(true);
    if (currentTemplate) {
      const enabledTemplate = templates
        .filter(item => item.id !== currentTemplate.id)
        .find(
          el =>
            el.is_enable &&
            el.action_type === selectValues.triggered_when &&
            el.template_type === selectValues.channel
        );

      const body = {
        template_type: selectValues.channel,
        action_type: selectValues.triggered_when,
        header: inputValues.emailHeader,
        message: inputValues.message,
        is_enable: enabledTemplate
          ? !enabledTemplate.is_enable
          : currentTemplate.is_enable
      };

      API.put(`/v1/templates/${currentTemplate.id}`, body)
        .then(res => {
          setTemplates(
            templates.map(template =>
              template.id === currentTemplate.id
                ? { ...res.data, ...body }
                : template
            )
          );
          handleClose();
          setLoading(false);
          enqueueSnackbar("Notification template edited.");
        })
        .catch(err => {
          if (err && err.status === 400 && err.data && err.data.errors[0]) {
            let field = err.data.errors[0].field;
            setErrors({
              [field]: err.data.errors[0].defaultMessage
            });
          }

          if (err.data.message === "URL in message") {
            enqueueSnackbar("Custom URL's are not allowed");
          } else {
            enqueueSnackbar("Something went wrong");
          }
          setLoading(false);
        });
    } else {
      API.post("/v1/templates", {
        template_type: selectValues.channel,
        action_type: selectValues.triggered_when,
        header: inputValues.emailHeader,
        message: inputValues.message
      })
        .then(res => {
          const triggerTitle = triggerOptions.find(
            triggerOption => triggerOption[0] === res.data.action_type
          )[1];
          const contentList = [];
          filteredTags.forEach(tag => {
            if (
              inputValues.message.includes(`{{${tag}}}`) ||
              inputValues.message.includes(`#${tag}`)
            ) {
              contentList.push(tag);
            }
          });
          if (/\[.*?\]\([^#]+?\)/gm.test(inputValues.message)) {
            contentList.push("custom_link");
          }
          mixpanelWrapper.track("Notification Template Create", {
            Trigger: triggerTitle,
            Channel: res.data.template_type,
            Content: contentList.join(", ")
          });
          setTemplates([res.data, ...templates]);
          handleClose();
          setLoading(false);
          enqueueSnackbar("Notification template added.");
        })
        .catch(err => {
          if (err && err.status === 400 && err.data && err.data.errors[0]) {
            let field = err.data.errors[0].field;
            setErrors({
              [field]: err.data.errors[0].defaultMessage
            });
          }

          if (err.data.message === "URL in message") {
            enqueueSnackbar("Custom URL's are not allowed");
          } else {
            enqueueSnackbar("Something went wrong");
          }
          setLoading(false);
        });
    }
  };

  const handleTagClick = tag => {
    let message;
    const tagsObj = {
      ...COMMON_TAGS,
      ...DELIVERED_TAGS,
      ...NOT_DELIVERED_TAGS
    };

    if (selectValues.channel === "email") {
      tag = { ...tagsObj, ...EMAIL_TAGS }[tag];
    } else {
      tag = { ...tagsObj, ...SMS_TAGS }[tag];
    }

    if (messageInputRef.current) {
      messageInputRef.current.focus();
    }

    if (!inputValues.message.length) {
      // message = tag.trim();
      document.execCommand("insertText", false, tag);
    } else {
      if (cursorPositionRef.current !== null) {
        // message =
        //   inputValues.message.slice(0, cursorPositionRef.current) +
        //   tag +
        //   inputValues.message.slice(-cursorPositionRef.current + 1);
        document.execCommand("insertText", false, tag);
      } else {
        message = `${inputValues.message}${tag}`;
        setInputValues({ ...inputValues, message });
      }
    }

    setSaveDisabled(false);
  };

  // Warn about unsaved changes before closing
  const warnClose = saveDisabled
    ? handleClose
    : () =>
        setChangesDialog({
          visible: true,
          action: handleClose
        });

  useEffect(() => {
    if (currentTemplate) {
      setInputValues({
        message: currentTemplate.message,
        emailHeader: currentTemplate.header
      });
      setSelectValues({
        triggered_when: currentTemplate.action_type,
        channel: currentTemplate.template_type
      });
    }
  }, [currentTemplate]);

  const { triggered_when, channel } = selectValues;

  return (
    <>
      <StyledDrawer
        open={open}
        noAnim
        handleClose={warnClose}
        loading={loading}
      >
        {/* Header */}
        <Box
          px={3}
          className={classes.header}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
        >
          <OptimizedTooltip placement="bottom" title="Close">
            <Box ml={-1} mr={2}>
              <IconButton onClick={warnClose} data-cy="tag-closeEdit">
                <BackIcon />
              </IconButton>
            </Box>
          </OptimizedTooltip>
          <Box alignSelf="center">
            <Typography variant="h5" className={classes.title}>
              {currentTemplate && currentTemplate.id
                ? "Edit template"
                : "Add template"}
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box pt={4} pb={1} pl={3} pr={5} mb={1.5} overflow="auto">
          {/* Details */}
          <Box display="flex" alignItems="center" mb={2}>
            <Typography className={classes.subtitle}>Details</Typography>
          </Box>

          {/* Form */}
          <Form
            data={[
              [
                {
                  labelId: "template-trigger",
                  selectId: "template-trigger-select",
                  label: "Trigger",
                  name: "triggered_when",
                  choose: false,
                  options: triggerOptions,
                  dataCy: "template-select-trigger",
                  onChange: e => handleSelect(e),
                  componentType: "select"
                }
              ]
            ]}
            inputValues={inputValues}
            selectValues={selectValues}
            errorValues={errors}
            handleChange={handleChange}
          />
          {triggered_when && (
            <div className={classes.infoDialog}>
              <InfoOutlinedIcon fontSize="small" />
              <Typography id="templateEdit-triggerInfo">
                {ACTION_TEXTS[triggered_when]}
              </Typography>
            </div>
          )}
          <Form
            data={[
              [
                {
                  labelId: "template-channel",
                  selectId: "template-channel-select",
                  label: "Channel",
                  name: "channel",
                  choose: false,
                  options: [
                    ["sms", "SMS"],
                    ["email", "Email"]
                  ],
                  dataCy: "template-select-channel",
                  onChange: e => handleSelect(e),
                  componentType: "select"
                }
              ]
            ]}
            inputValues={inputValues}
            selectValues={selectValues}
            errorValues={errors}
            handleChange={handleChange}
          />

          {channel === "email" && (
            <Form
              data={[
                [
                  {
                    renderInputField: true,
                    label: "Email header",
                    name: "emailHeader",
                    type: "text",
                    dataCy: "email-header",
                    maxLength: 132,
                    error: inputValues.emailHeader?.length >= maxHeaderLength,
                    onChange: e =>
                      (e.target.value = e.target.value.slice(
                        0,
                        maxHeaderLength
                      )),
                    helperText: (
                      <span
                        style={{
                          display: "flex",
                          component: "span",
                          justifyContent: "space-between"
                        }}
                      >
                        <span>
                          {inputValues.emailHeader?.length >= maxHeaderLength &&
                            "Maximum allowed email header length reached"}
                        </span>
                        <span>{`${
                          inputValues.emailHeader
                            ? inputValues.emailHeader.length
                            : 0
                        }/${maxHeaderLength}`}</span>
                      </span>
                    )
                  }
                ]
              ]}
              selectValues={selectValues}
              inputValues={inputValues}
              errorValues={errors}
              handleChange={handleChange}
            />
          )}

          {/* Content */}
          <Box mb={2} mt={4}>
            <Typography className={classes.subtitle}>Content</Typography>
            <Typography className={classes.subInfo}>
              Add the following components to your message by clicking on them.
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" mb={4}>
            {filteredTags.map((el, i) => (
              <Chip
                key={i}
                label={el}
                className={classes.chip}
                onMouseDown={() => {
                  // var startPos = messageInputRef.current.selectionStart;
                  cursorPositionRef.current =
                    messageInputRef.current.selectionStart;
                  // var endPos = messageInputRef.current.selectionEnd;
                  // console.log(startPos + ", " + endPos);
                  // console.log("MOUSE DOWN");
                }}
                onClick={() => handleTagClick(el)}
                id={`templateComponent-${el.toLowerCase()}`}
              />
            ))}
          </Box>

          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "Message",
                  name: "message",
                  type: "text",
                  inputRef: messageInputRef,
                  multiline: true,
                  dataCy: "message"
                }
              ]
            ]}
            selectValues={selectValues}
            inputValues={inputValues}
            errorValues={errors}
            handleChange={handleChange}
          />
        </Box>

        {/* Actions */}
        <EditDialogActions
          section="templates"
          handleClose={warnClose}
          handleClick={handleSave}
          disabled={saveDisabled}
        />
        <UnsavedChangesDialog
          open={changesDialog.visible}
          handleClose={() => {
            setChangesDialog({ visible: false, action: () => {} });
          }}
          action={() => {
            changesDialog.action();
            setSaveDisabled(true);
          }}
        />
      </StyledDrawer>
      <ConfirmDialog
        open={confirmDialogState.open}
        handleClose={() => setConfirmDialogState({ open: false })}
        action={() => {
          setInputValues({ emailHeader: "", message: "" });
          setSelectValues({
            ...selectValues,
            [confirmDialogState.name]: confirmDialogState.value
          });
          setSaveDisabled(false);
        }}
        title={"Are you sure?"}
        body={
          "If you change the channel, the content within your message field will be cleared."
        }
        confirmText={"Yes, I'm sure"}
      />
    </>
  );
}

export default TemplatesEditDialog;
